export const digitsOnly = (v) => String(v || '').replace(/\D/g, '');

const strOrEmpty = (v) => (v == null ? '' : String(v));
const splitNameParts = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return {
    first: parts[0] || '',
    middle: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
    last: parts.length > 1 ? parts[parts.length - 1] : '',
  };
};

export const normalizeCommissionsPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.groups)) return data.groups;
  if (data && Array.isArray(data.commissions)) return data.commissions;
  return [];
};

export const normalizeContributionsPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.contributions)) return data.contributions;
  return [];
};

export const normalizeGroupsFromPeople = (profile) => {
  const raw = profile?.metadata?.groups;
  if (!Array.isArray(raw)) return [];
  return raw.filter((g) => typeof g === 'string' && g.trim());
};

export const buildUsersRoleIndex = (users) => {
  const safeUsers = Array.isArray(users) ? users : [];
  return {
    byMember: safeUsers.reduce((acc, u) => {
      const key = u?.member_id || u?.person_id;
      if (key && u?.role) acc[String(key)] = String(u.role);
      return acc;
    }, {}),
    byEmail: safeUsers.reduce((acc, u) => {
      const key = String(u?.email || '').trim().toLowerCase();
      if (key && u?.role) acc[key] = String(u.role);
      return acc;
    }, {}),
    byPhone: safeUsers.reduce((acc, u) => {
      const key = digitsOnly(u?.phone);
      if (key && u?.role) acc[key] = String(u.role);
      return acc;
    }, {}),
  };
};

export const resolveIamRoleForPerson = (person, usersIndex) => {
  const id = String(person?.id || '');
  const email = String(person?.email || '').trim().toLowerCase();
  const phone = digitsOnly(person?.phone);
  return (
    usersIndex?.byMember?.[id] ||
    usersIndex?.byEmail?.[email] ||
    usersIndex?.byPhone?.[phone] ||
    ''
  );
};

/**
 * Index `position_id` (positions projet) par membre People — même clés que les rôles IAM.
 * Filtre optionnel par `projectId` pour éviter d'afficher la position d'un autre projet.
 */
export const buildUsersPositionIndex = (users, projectId = null) => {
  const safeUsers = Array.isArray(users) ? users : [];
  const scoped =
    projectId != null && String(projectId).trim() !== ''
      ? safeUsers.filter(
          (u) => !u?.project_id || String(u.project_id).trim() === String(projectId).trim()
        )
      : safeUsers;
  const put = (acc, key, posId) => {
    if (key && posId != null && String(posId).trim() !== '') acc[String(key)] = String(posId).trim();
  };
  return {
    byMember: scoped.reduce((acc, u) => {
      put(acc, u?.member_id || u?.person_id, u?.position_id);
      return acc;
    }, {}),
    byEmail: scoped.reduce((acc, u) => {
      const key = String(u?.email || '').trim().toLowerCase();
      put(acc, key, u?.position_id);
      return acc;
    }, {}),
    byPhone: scoped.reduce((acc, u) => {
      const key = digitsOnly(u?.phone);
      put(acc, key, u?.position_id);
      return acc;
    }, {}),
  };
};

/** Dernier `position_id` connu pour ce profil People (utilisateur IAM lié au projet courant). */
export const resolveProjectPositionIdForPerson = (person, positionIndex) => {
  const id = String(person?.id || '');
  const email = String(person?.email || '').trim().toLowerCase();
  const phone = digitsOnly(person?.phone);
  return (
    positionIndex?.byMember?.[id] ||
    positionIndex?.byEmail?.[email] ||
    positionIndex?.byPhone?.[phone] ||
    ''
  );
};

export const findLinkedIamUserFromUsers = (personId, member, users) => {
  const sid = String(personId);
  const em = String(member?.email || '').trim().toLowerCase();
  const ph = digitsOnly(member?.phone);
  const rows = Array.isArray(users) ? users : [];
  for (const u of rows) {
    if (!u) continue;
    if (String(u.member_id || '') === sid || String(u.person_id || '') === sid) return u;
    if (em && String(u.email || '').trim().toLowerCase() === em) return u;
    if (ph && digitsOnly(u.phone || '') === ph) return u;
    if (member?.phone && String(u.username || '').trim() === String(member.phone).trim()) return u;
  }
  return null;
};

export const toUsersArray = (raw) => (Array.isArray(raw) ? raw : (raw?.users || []));

export const mapPeopleToMemberRow = (p) => {
  const groups = normalizeGroupsFromPeople(p);
  const profile = p?.metadata?.profile || {};
  const split = splitNameParts(p?.name || p?.full_name || '');
  const address = profile?.address || {};
  const preferences = p?.metadata?.preferences || {};
  const documentIds = Array.isArray(p?.document_file_ids) ? p.document_file_ids : [];
  const memberNumber = strOrEmpty(
    p.member_number || profile?.member_number
  );
  return {
    id: p.id,
    member_number: memberNumber,
    name: p.full_name || p.name || p.display_name || p.email || 'Sans nom',
    email: p.email != null && p.email !== '' ? String(p.email) : '',
    phone: p.phone != null && p.phone !== '' ? String(p.phone) : '',
    join_date: p?.metadata?.join_date
      ? String(p.metadata.join_date).slice(0, 10)
      : p.created_at
        ? String(p.created_at).slice(0, 10)
        : '',
    status:
      p.is_active === false ||
      p.active === false ||
      String(p?.status || '').toLowerCase() === 'inactive'
        ? 'inactive'
        : 'active',
    is_active: !(p.active === false || p.is_active === false),
    commission: groups[0] || p?.metadata?.commission || p.commission || '',
    groups,
    type: String(p?.type || 'member'),
    document_file_ids: documentIds,
    first_name: strOrEmpty(profile?.first_name || split.first),
    last_name: strOrEmpty(profile?.last_name || split.last),
    middle_name: strOrEmpty(profile?.middle_name || split.middle),
    date_of_birth: strOrEmpty(profile?.date_of_birth),
    place_of_birth: strOrEmpty(profile?.place_of_birth),
    sex: strOrEmpty(profile?.sex),
    marital_status: strOrEmpty(profile?.marital_status),
    nationality: strOrEmpty(profile?.nationality),
    profession: strOrEmpty(profile?.profession),
    id_number: strOrEmpty(profile?.id_number),
    photo_file_id: strOrEmpty(profile?.photo_file_id),
    identity_document_type: strOrEmpty(profile?.identity_document_type),
    identity_document_number: strOrEmpty(
      profile?.identity_document_number || profile?.id_number
    ),
    identity_document_expiration_date: strOrEmpty(
      profile?.identity_document_expiration_date
    ),
    identity_document_file_id: strOrEmpty(profile?.identity_document_file_id),
    address_line1: strOrEmpty(address?.line1),
    address_line2: strOrEmpty(address?.line2),
    address_city: strOrEmpty(address?.city),
    address_country: strOrEmpty(address?.country),
    address_postal_code: strOrEmpty(address?.postal_code),
    preferred_language: strOrEmpty(preferences?.langue || preferences?.language),
    source: 'people',
    created_at: p.created_at,
  };
};

/** Liste People (`GET /v1/people/people`) → lignes membre Entrea (sélecteurs, libellés). */
export const mapPeopleListResponse = (data) => {
  const raw = data?.people || [];
  return raw.map((p) => mapPeopleToMemberRow(p));
};

/** IDs `document_file_ids` People : complémentaires + photo + pièce (dédoublonnés). */
export const mergePeopleDocumentFileIds = (formData) => {
  const other = Array.isArray(formData.other_document_file_ids)
    ? formData.other_document_file_ids.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const merged = [
    ...new Set(
      [...other, formData.photo_file_id, formData.identity_document_file_id]
        .map((x) => String(x || '').trim())
        .filter(Boolean)
    ),
  ];
  return merged.length ? merged : undefined;
};

/** Fiches `document_file_ids` hors photo / pièce (pour UI « complémentaires »). */
export const getSupplementaryDocumentIdsFromMemberRow = (member) => {
  if (!member) return [];
  const known = new Set(
    [String(member.photo_file_id || ''), String(member.identity_document_file_id || '')].filter(Boolean)
  );
  const raw = Array.isArray(member.document_file_ids) ? member.document_file_ids : [];
  return raw.map((x) => String(x || '').trim()).filter((fid) => fid && !known.has(fid));
};

export const buildPeopleMemberPayload = (formData, currentProjectId, options = {}) => {
  const selfEdit = Boolean(options.selfEdit);
  const composedName = [
    String(formData.first_name || '').trim(),
    String(formData.middle_name || '').trim(),
    String(formData.last_name || '').trim(),
  ]
    .filter(Boolean)
    .join(' ');
  const resolvedName = composedName || String(formData.name || '').trim();
  const address =
    formData.address_line1 ||
    formData.address_line2 ||
    formData.address_city ||
    formData.address_country ||
    formData.address_postal_code
      ? {
          ...(formData.address_line1 ? { line1: formData.address_line1 } : {}),
          ...(formData.address_line2 ? { line2: formData.address_line2 } : {}),
          ...(formData.address_city ? { city: formData.address_city } : {}),
          ...(formData.address_country ? { country: formData.address_country } : {}),
          ...(formData.address_postal_code
            ? { postal_code: formData.address_postal_code }
            : {}),
        }
      : undefined;

  const profile = {
    ...(formData.first_name ? { first_name: formData.first_name } : {}),
    ...(formData.last_name ? { last_name: formData.last_name } : {}),
    ...(formData.middle_name ? { middle_name: formData.middle_name } : {}),
    ...(formData.date_of_birth ? { date_of_birth: formData.date_of_birth } : {}),
    ...(formData.place_of_birth ? { place_of_birth: formData.place_of_birth } : {}),
    ...(formData.sex ? { sex: formData.sex } : {}),
    ...(formData.marital_status ? { marital_status: formData.marital_status } : {}),
    ...(formData.nationality ? { nationality: formData.nationality } : {}),
    ...(formData.profession ? { profession: formData.profession } : {}),
    ...(formData.id_number ? { id_number: formData.id_number } : {}),
    ...(formData.photo_file_id ? { photo_file_id: formData.photo_file_id } : {}),
    ...(formData.identity_document_type
      ? { identity_document_type: formData.identity_document_type }
      : {}),
    ...(formData.identity_document_number
      ? { identity_document_number: formData.identity_document_number }
      : {}),
    ...(formData.identity_document_expiration_date
      ? {
          identity_document_expiration_date:
            formData.identity_document_expiration_date,
        }
      : {}),
    ...(formData.identity_document_file_id
      ? { identity_document_file_id: formData.identity_document_file_id }
      : {}),
    ...(address ? { address } : {}),
  };

  const metadata = {
    source_app: 'entrea',
    project_id: currentProjectId || undefined,
    ...(!selfEdit
      ? {
          groups: formData.commission ? [formData.commission] : [],
          ...(formData.join_date ? { join_date: formData.join_date } : {}),
        }
      : {}),
    ...(Object.keys(profile).length > 0 ? { profile } : {}),
    ...(formData.preferred_language
      ? { preferences: { langue: formData.preferred_language } }
      : {}),
  };

  return {
    name: resolvedName,
    email: formData.email || undefined,
    phone: formData.phone || undefined,
    ...(!selfEdit ? { type: formData.type || 'member' } : {}),
    metadata,
    document_file_ids: mergePeopleDocumentFileIds(formData),
  };
};

